import { useSearchParams } from 'next/navigation';
import React, { useMemo, useCallback } from 'react';
import { getToolSideList } from '@/features/tool/data';
import { Popover, PopoverContent, PopoverTrigger } from '@heroui/popover';
import { NextUI } from '@/components/NextUI';
import Icon from '@/components/Common/Icons/Icon';
import { useTranslations } from 'next-intl';

const defaultToolInfo = {
  title: 'PDF Tool',
  description: 'Process your PDF files with our advanced tools.',
};

function ToolInfoPopover() {
  const searchParams = useSearchParams();
  const currentTool = searchParams.get('tool');
  const t = useTranslations();
  const toolSideList = getToolSideList(t);

  const findToolInfo = useCallback(() => {
    const mainTool = toolSideList.find((tool) => tool.key === currentTool);

    if (mainTool?.toolInfo) {
      return mainTool.toolInfo;
    }

    for (const tool of toolSideList) {
      if (tool.subMenu) {
        const subTool = tool.subMenu.find((sub) => sub.link === currentTool);
        if (subTool?.toolInfo) {
          return subTool.toolInfo;
        }
      }
    }

    return defaultToolInfo;
  }, [currentTool, toolSideList]);

  const toolInfo = useMemo(() => findToolInfo(), [findToolInfo]);

  return (
    <Popover placement="bottom">
      <PopoverTrigger>
        <NextUI.Button
          isIconOnly
          variant={null}
          className="!top-8 !right-8 !hidden md:!block !w-12 !h-12 !absolute"
          radius="full"
        >
          <Icon name="ToolAppInfoIcon" />
        </NextUI.Button>
      </PopoverTrigger>
      <PopoverContent className="p-0">
        <div className="p-10 max-w-[366px]">
          <h6 className="font-semibold">{toolInfo.title}</h6>
          <div className="mt-6 list-decimal text-gray-medium body-normal">
            {toolInfo.description}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default React.memo(ToolInfoPopover);
